import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Phone, MessageCircle, CheckCircle, MapPin, Clock, ChevronDown, ChevronUp } from 'lucide-react';

interface TimelineStep {
  key:     string;
  label:   string;
  desc:    string;
  done:    boolean;
  current: boolean;
  date:    string | null;
}

interface TrackerData {
  lead: {
    firstname:                 string;
    status:                    string;
    vloertype:                 string | null;
    oppervlakte:               number | null;
    planned_installation_date: string | null;
  };
  quote:         { total_incl_btw: number; signed_at: string } | null;
  timeline:      TimelineStep[];
  current_stage: { key: string; label: string; desc: string } | null;
  next_step:     { label: string; description: string } | null;
  operator: {
    company_name:     string;
    sender_name:      string;
    sender_phone:     string;
    sender_photo_url: string | null;
  };
  recent_projects: { neighborhood: string; type: string; m2: number; desc: string }[];
}

function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  const months = ['januari','februari','maart','april','mei','juni',
    'juli','augustus','september','oktober','november','december'];
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

export default function ProjectTrackerPage() {
  const { token } = useParams<{ token: string }>();
  const [data,    setData]    = useState<TrackerData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set());

  const SUPABASE_URL     = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
  const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

  useEffect(() => {
    if (!token) return;
    fetch(`${SUPABASE_URL}/functions/v1/project-tracker?token=${token}`, {
      headers: {
        'apikey':        SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      },
    })
      .then(r => r.json())
      .then(d => { if (d.error) setError(d.error); else setData(d); })
      .catch(() => setError('Kon de projectstatus niet laden.'))
      .finally(() => setLoading(false));
  }, [token]);

  const bg          = '#EDE8E0';
  const card        = '#F5F0EA';
  const border      = '#E8E0D5';
  const ink         = '#1C1917';
  const muted       = '#8A7E75';
  const green       = '#3D7C3D';
  const greenBg     = 'rgba(61,124,61,0.08)';
  const greenBorder = 'rgba(61,124,61,0.25)';

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 24, height: 24, borderRadius: '50%', border: `2px solid ${border}`, borderTopColor: green, animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ minHeight: '100vh', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
        <div style={{ background: card, borderRadius: 12, padding: '32px 24px', textAlign: 'center', maxWidth: 360, border: `1px solid ${border}` }}>
          <p style={{ color: ink, fontSize: 17, fontWeight: 600, marginBottom: 8 }}>Project niet gevonden</p>
          <p style={{ color: muted, fontSize: 14, lineHeight: 1.6 }}>
            De link is mogelijk verlopen of onjuist. Neem contact op via WhatsApp.
          </p>
        </div>
      </div>
    );
  }

  const { lead, quote, timeline, operator, next_step, recent_projects } = data;
  const waNumber = (operator.sender_phone || '').replace(/\s/g, '').replace(/^0/, '+31');
  const waLink   = `https://wa.me/${waNumber.replace('+', '')}`;

  return (
    <div style={{ minHeight: '100vh', background: bg, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
      <div style={{ maxWidth: 560, margin: '0 auto', padding: '32px 16px 64px' }}>

        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <p style={{ fontSize: 11, letterSpacing: '2px', textTransform: 'uppercase', color: muted, margin: '0 0 4px' }}>
            {operator.company_name}
          </p>
          <h1 style={{ fontSize: 24, fontWeight: 600, color: ink, margin: 0, letterSpacing: '-0.02em' }}>
            Hoi {lead.firstname}!
          </h1>
          <p style={{ fontSize: 15, color: '#3D3530', marginTop: 6, lineHeight: 1.6 }}>
            Hier kun je de voortgang van jouw project volgen.
          </p>
        </div>

        {/* Project summary card */}
        <div style={{ background: card, borderRadius: 12, border: `1px solid ${border}`, marginBottom: 16, overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: `1px solid ${border}` }}>
            <p style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: muted, margin: '0 0 10px' }}>
              Jouw project
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 24px' }}>
              {lead.vloertype && (
                <div>
                  <p style={{ fontSize: 11, color: muted, margin: '0 0 2px' }}>Type vloer</p>
                  <p style={{ fontSize: 14, fontWeight: 500, color: ink, margin: 0 }}>{lead.vloertype}</p>
                </div>
              )}
              {lead.oppervlakte && (
                <div>
                  <p style={{ fontSize: 11, color: muted, margin: '0 0 2px' }}>Oppervlakte</p>
                  <p style={{ fontSize: 14, fontWeight: 500, color: ink, margin: 0 }}>{lead.oppervlakte} m²</p>
                </div>
              )}
              {quote && (
                <div>
                  <p style={{ fontSize: 11, color: muted, margin: '0 0 2px' }}>Offertebedrag</p>
                  <p style={{ fontSize: 14, fontWeight: 500, color: green, margin: 0 }}>
                    €{Math.round(quote.total_incl_btw).toLocaleString('nl-NL')} incl. btw
                  </p>
                </div>
              )}
              {lead.planned_installation_date && (
                <div>
                  <p style={{ fontSize: 11, color: muted, margin: '0 0 2px' }}>Installatieweek</p>
                  <p style={{ fontSize: 14, fontWeight: 500, color: ink, margin: 0 }}>
                    {formatDate(lead.planned_installation_date)}
                  </p>
                </div>
              )}
            </div>
          </div>

          {next_step && (
            <div style={{ padding: '14px 20px', background: greenBg, borderTop: `1px solid ${greenBorder}` }}>
              <p style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: green, margin: '0 0 4px' }}>
                Volgende stap
              </p>
              <p style={{ fontSize: 13, color: '#2D5C2D', margin: 0, lineHeight: 1.5 }}>
                {next_step.description}
              </p>
            </div>
          )}
        </div>

        {/* Timeline */}
        <div style={{ background: card, borderRadius: 12, border: `1px solid ${border}`, marginBottom: 16, overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: `1px solid ${border}` }}>
            <p style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: muted, margin: 0 }}>
              Voortgang
            </p>
          </div>

          {timeline.map((step, i) => {
            const isExpanded = expandedSteps.has(i);
            const toggleExpand = () => {
              const s = new Set(expandedSteps);
              if (s.has(i)) s.delete(i); else s.add(i);
              setExpandedSteps(s);
            };

            return (
              <div key={step.key} style={{ borderBottom: i < timeline.length - 1 ? `1px solid ${border}` : 'none' }}>
                <div
                  onClick={toggleExpand}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '14px 20px',
                    cursor: 'pointer',
                    borderLeft: step.current ? `3px solid ${ink}` : '3px solid transparent',
                  }}
                >
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: step.done ? green : step.current ? ink : border,
                    border: `2px solid ${step.done ? green : step.current ? ink : '#C8BEB4'}`,
                  }}>
                    {step.done ? (
                      <CheckCircle size={14} color="#fff" />
                    ) : (
                      <span style={{ fontSize: 12, fontWeight: 600, color: step.current ? '#fff' : muted }}>
                        {i + 1}
                      </span>
                    )}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{
                      fontSize: 14,
                      fontWeight: step.current ? 600 : 400,
                      color: step.done ? muted : step.current ? ink : '#A09080',
                      margin: 0, lineHeight: 1.3,
                    }}>
                      {step.label}
                    </p>
                    {step.date && step.done && (
                      <p style={{ fontSize: 12, color: muted, margin: '2px 0 0' }}>
                        {formatDate(step.date)}
                      </p>
                    )}
                  </div>

                  <div style={{ color: muted, flexShrink: 0 }}>
                    {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </div>
                </div>

                {isExpanded && (
                  <div style={{ padding: '0 20px 14px', paddingLeft: 20 + 28 + 12 }}>
                    <p style={{ fontSize: 13, color: '#5A4E48', margin: 0, lineHeight: 1.6 }}>
                      {step.desc}
                    </p>
                    {step.current && step.date && (
                      <div style={{
                        marginTop: 10, padding: '10px 14px',
                        background: '#fff', borderRadius: 8,
                        border: `1px solid ${border}`,
                        display: 'flex', alignItems: 'center', gap: 8,
                      }}>
                        <Clock size={13} style={{ color: green, flexShrink: 0 }} />
                        <span style={{ fontSize: 13, color: ink }}>
                          {step.key.includes('levering') || step.key.includes('werkbezoek')
                            ? `Ingepland: ${formatDate(step.date)}`
                            : formatDate(step.date)}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Vakman card — shown when werkbezoek or levering is planned */}
        {(lead.status === 'werkbezoek_gepland' || lead.status === 'levering_gepland') && (
          <div style={{ background: card, borderRadius: 12, border: `1px solid ${border}`, marginBottom: 16, overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: `1px solid ${border}` }}>
              <p style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: muted, margin: 0 }}>
                {lead.status === 'werkbezoek_gepland' ? 'Wie komt er langs' : 'Jouw installateur'}
              </p>
            </div>
            <div style={{ padding: '16px 20px', display: 'flex', gap: 16, alignItems: 'flex-start' }}>
              <div style={{
                width: 52, height: 52, borderRadius: '50%', flexShrink: 0,
                background: '#D4C9BC', display: 'flex', alignItems: 'center', justifyContent: 'center',
                overflow: 'hidden',
              }}>
                {operator.sender_photo_url ? (
                  <img src={operator.sender_photo_url} alt={operator.sender_name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <span style={{ fontSize: 20, fontWeight: 600, color: '#8A7E75' }}>
                    {operator.sender_name.charAt(0)}
                  </span>
                )}
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: 16, fontWeight: 600, color: ink, margin: '0 0 2px' }}>
                  {operator.sender_name}
                </p>
                <p style={{ fontSize: 13, color: muted, margin: '0 0 8px' }}>
                  {operator.company_name} · Amsterdam
                </p>
                <p style={{ fontSize: 13, color: '#3D3530', lineHeight: 1.6, margin: 0 }}>
                  {lead.status === 'werkbezoek_gepland'
                    ? `${operator.sender_name} legt al meer dan 10 jaar PVC vloeren in Amsterdam. Hij meet nauwkeurig op en adviseert je over de beste opties voor jouw situatie.`
                    : `${operator.sender_name} en zijn team installeren jouw vloer. Ze zijn om 8:00 uur aanwezig. Je wordt de dag ervoor nog even gebeld om de tijd te bevestigen.`}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Recent projects */}
        <div style={{ background: card, borderRadius: 12, border: `1px solid ${border}`, marginBottom: 16, overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: `1px solid ${border}` }}>
            <p style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: muted, margin: 0 }}>
              Recente projecten in Amsterdam
            </p>
          </div>
          {recent_projects.map((project, i) => (
            <div key={i} style={{
              padding: '14px 20px',
              borderBottom: i < recent_projects.length - 1 ? `1px solid ${border}` : 'none',
              display: 'flex', alignItems: 'center', gap: 12,
            }}>
              <div style={{
                width: 44, height: 44, borderRadius: 8, flexShrink: 0,
                background: '#D4C9BC', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <MapPin size={16} style={{ color: '#8A7E75' }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                  <p style={{ fontSize: 14, fontWeight: 500, color: ink, margin: 0 }}>
                    {project.type}
                  </p>
                  <span style={{ fontSize: 11, color: muted }}>·</span>
                  <p style={{ fontSize: 12, color: muted, margin: 0 }}>{project.m2} m²</p>
                </div>
                <p style={{ fontSize: 12, color: muted, margin: 0 }}>
                  {project.neighborhood}, Amsterdam · {project.desc}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Contact card */}
        <div style={{ background: card, borderRadius: 12, border: `1px solid ${border}`, overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: `1px solid ${border}` }}>
            <p style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: muted, margin: 0 }}>
              Contact
            </p>
          </div>
          <div style={{ padding: '16px 20px', display: 'flex', gap: 10 }}>
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                padding: '12px 0', borderRadius: 10,
                background: 'rgba(37,211,102,0.10)',
                border: '1px solid rgba(37,211,102,0.25)',
                color: '#1A7A3A',
                fontSize: 14, fontWeight: 600, textDecoration: 'none',
              }}
            >
              <MessageCircle size={16} />
              WhatsApp
            </a>
            <a
              href={`tel:${operator.sender_phone}`}
              style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                padding: '12px 0', borderRadius: 10,
                background: '#fff',
                border: `1px solid ${border}`,
                color: ink,
                fontSize: 14, fontWeight: 500, textDecoration: 'none',
              }}
            >
              <Phone size={16} />
              Bellen
            </a>
          </div>
        </div>

        <p style={{ fontSize: 11, color: '#B0A89E', textAlign: 'center', marginTop: 24 }}>
          {operator.company_name} · Amsterdam
        </p>

      </div>
    </div>
  );
}
